import { useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@noor/lib';
import { OFFERS_MODES, type OffersMode } from '@/entities/order';

interface OffersModeSwitchProps {
  value: OffersMode;
  onChange: (mode: OffersMode) => void;
}

/**
 * Two-way segmented control for the offers source. A radio group rather than
 * tabs: it swaps the data behind ONE map instead of showing separate panels, so
 * there is no panel to point `aria-controls` at. Arrow keys move and select
 * (roving tabindex), as for native radios.
 */
export function OffersModeSwitch({ value, onChange }: OffersModeSwitchProps) {
  const { t } = useTranslation('orders');
  const groupRef = useRef<HTMLDivElement>(null);

  const select = (index: number) => {
    const next = OFFERS_MODES[(index + OFFERS_MODES.length) % OFFERS_MODES.length];
    if (!next) return;
    onChange(next);
    // Move focus with the selection, as native radios do.
    const radios = groupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[OFFERS_MODES.indexOf(next)]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        select(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        select(index - 1);
        break;
      case 'Home':
        select(0);
        break;
      case 'End':
        select(OFFERS_MODES.length - 1);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={t('offers.mode.label')}
      className="inline-flex h-8 shrink-0 items-center gap-0.5 self-start rounded-lg bg-tabs-list-bg p-0.5"
    >
      {OFFERS_MODES.map((mode, index) => {
        const checked = mode === value;
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            title={t(`offers.mode.${mode}Hint`)}
            onClick={() => onChange(mode)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'h-7 cursor-pointer rounded-md px-3 text-xs font-medium whitespace-nowrap text-tabs-trigger-fg transition-all duration-150 ease-out outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
              checked
                ? 'bg-tabs-trigger-bg-active text-tabs-trigger-fg-active shadow-sm'
                : 'hover:text-foreground',
            )}
          >
            {t(`offers.mode.${mode}`)}
          </button>
        );
      })}
    </div>
  );
}
